import { zodResolver } from "@hookform/resolvers/zod";
import DomainHelper from "helpers/DomainHelper";
import { useEffect, useId } from "react";
import { useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  addCompanyPageUrl,
  deleteCompanyPageUrl,
  editCompanyPageUrl,
  getCompanyPageUrls,
  resetDeleteCompanyPageUrlState,
  resetGetCompanyPageUrlState,
} from "store/slices/company.slice";
import { ICompanyPage } from "types/company/company.type";
import { SegmentEventENUM } from "utils/enums/segmentENUM";
import { toastHelper } from "utils/helper";
import { useModal } from "utils/hooks/useModal";
import { cleanObject } from "utils/object";
import { schema, schemaProps } from "../schema";

export const usePageIntent = () => {
  const dispatch = useAppDispatch();
  const companyPages = useAppSelector(
    (state) => state.company.companyPages.data
  );
  const isLoading = useAppSelector(
    (state) => state.company.companyPages.isLoading
  );
  const editOrUpdateModal = useModal();
  const confirmDeleteModal = useModal();
  const id = useId();
  const { data: user } = useAppSelector((state) => state.user);

  useEffect(() => {
    dispatch(resetGetCompanyPageUrlState());
    dispatch(getCompanyPageUrls());
  }, []);

  const form = useForm({
    defaultValues: {
      pageCondition: "contains",
      pageUrl: "",
      pageIntent: "medium",
    },
    resolver: zodResolver(schema),
  });

  const extractUrl = (pageUrl: string): string => {
    if (form.watch("pageCondition") === "contains") {
      const url = new URL(pageUrl, DomainHelper.getAPPUrl());
      return url.pathname;
    }
    return pageUrl;
  };
  const sendTrack = (eventEnum: SegmentEventENUM) => {
    const leadPagePayload = {
      firstname: user.firstName,
      lastname: user.lastName,
      email: user.email,
      pageIntent: form.watch("pageIntent"),
      pageUrl: form.watch("pageUrl") || "/",
    };
    const cleanedFilters = cleanObject(leadPagePayload);
    window.analytics.track(eventEnum, cleanedFilters);
  };
  const handleFormSubmit = async (props: schemaProps) => {
    toastHelper({
      toastId: id,
      type: "info",
      message: props.id ? "Updating page url..." : "Adding new page url...",
    });
    try {
      if (props.id) {
        await dispatch(
          editCompanyPageUrl({
            id: String(props.id),
            pageUrl: extractUrl(props.pageUrl),
            pageIntent: props.pageIntent,
          })
        ).unwrap();
      } else {
        await dispatch(
          addCompanyPageUrl({
            pageUrl: extractUrl(props.pageUrl),
            pageIntent: props.pageIntent,
          })
        ).unwrap();
      }
      sendTrack(SegmentEventENUM.LEAD_PAGE_ADDED);
      toastHelper({
        toastId: id,
        type: "success",
        message: props.id
          ? "The page url has been successfully modified"
          : "The page url has been successfully added",
      });
    } catch (error) {
      toastHelper({
        toastId: id,
        type: "error",
        message: props.id
          ? "Failed to update page url."
          : "Failed to add page url.",
      });
    } finally {
      form.reset();
      editOrUpdateModal.closeModal();
    }
  };
  const handleDeleteItem = async () => {
    toastHelper({ toastId: id, type: "info", message: "Deleting page url..." });
    try {
      await dispatch(
        deleteCompanyPageUrl(String(confirmDeleteModal.isOpen))
      ).unwrap();
      sendTrack(SegmentEventENUM.LEAD_PAGE_DELETED);
      toastHelper({
        toastId: id,
        type: "success",
        message: "The page url has been successfully removed",
      });
      confirmDeleteModal.closeModal();
      dispatch(resetDeleteCompanyPageUrlState());
    } catch (error) {
      toastHelper({
        toastId: id,
        type: "error",
        message: "Failed to delete page url.",
      });
    }
  };
  const handleEditItem = (props: ICompanyPage) => {
    form.setValue("id", props.clientPage.id);
    form.setValue("pageCondition", "is");
    props.clientPage.pageUrl &&
      form.setValue("pageUrl", props.clientPage.pageUrl);
    props.clientPage.pageIntent &&
      form.setValue("pageIntent", props.clientPage.pageIntent);
    editOrUpdateModal.openModal();
  };

  return {
    companyPages,
    isLoading,
    form,
    editOrUpdateModal,
    confirmDeleteModal,
    handleFormSubmit,
    handleDeleteItem,
    handleEditItem,
  };
};
